import { formatDate } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import {
  Badge,
  confirm,
  createSortableColumn,
  DataTable,
  Progress,
  type TanstackReactTable,
} from "@devhop/ui";
import { EyeIcon, TrashIcon } from "lucide-react";
import type { PrivateLessonPackageDTO } from "../../domain/repo/list-packages";

type PackageRow = PrivateLessonPackageDTO;

type PackagesTableProps = {
  data: PackageRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView: (pkg: PackageRow) => void;
  onCancel: (id: string) => void;
};

export function PackagesTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onCancel,
}: PackagesTableProps) {
  const columns: TanstackReactTable.ColumnDef<PackageRow>[] = [
    {
      header: "ນັກຮຽນ",
      accessorKey: "studentName",
      cell: ({ row }) => (
        <div>
          <div className="font-semibold">{row.original.studentName}</div>
          <div className="text-muted-foreground text-xs">
            {row.original.studentMemberId}
          </div>
        </div>
      ),
    },
    {
      header: "Template",
      accessorKey: "templateName",
      cell: ({ row }) => row.original.templateName,
    },
    {
      header: "ຊົ່ວໂມງ",
      id: "hours",
      cell: ({ row }) => {
        const { totalHours, remainingHours } = row.original;
        const usedHours = totalHours - remainingHours;
        const progressPercentage =
          totalHours > 0 ? (usedHours / totalHours) * 100 : 0;

        return (
          <div className="flex min-w-[100px] flex-col items-center space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {usedHours} / {totalHours}
              </span>
              {/* <span className="font-semibold">
                {progressPercentage.toFixed(0)}%
              </span> */}
            </div>
            <Progress value={progressPercentage} className="h-2" />
          </div>
        );
      },
    },
    createSortableColumn("startDate", "ວັນທີເລີ່ມຕົ້ນ", {
      cell: ({ row }) => formatDate(row.original.startDate),
    }),
    createSortableColumn("expireDate", "ວັນທີໝົດອາຍຸ", {
      cell: ({ row }) => formatDate(row.original.expireDate),
    }),
    {
      header: "ສະຖານະ",
      accessorKey: "status",
      cell: ({ row }) => {
        const status = row.original.status;
        const variant =
          status === "active"
            ? "default"
            : status === "expired"
              ? "destructive"
              : status === "used_up"
                ? "secondary"
                : status === "canceled"
                  ? "outline"
                  : "outline";
        const label =
          status === "active"
            ? "ໃຊ້ງານ"
            : status === "expired"
              ? "ໝົດອາຍຸ"
              : status === "used_up"
                ? "ໃຊ້ຄົບແລ້ວ"
                : status === "pending_cancel"
                  ? "ລໍຖ້າຍົກເລີກ"
                  : status === "canceled"
                    ? "ຍົກເລີກ"
                    : status;
        return <Badge variant={variant}>{label}</Badge>;
      },
    },
    createSortableColumn("purchasedAt", "ວັນທີຊື້", {
      cell: ({ row }) => formatDate(row.original.purchasedAt),
    }),
    {
      id: "actions",
      header: "",
      cell: ({ row }: { row: { original: PackageRow } }) => {
        const pkg = row.original;
        const canCancel = pkg.status === "active";
        const actions = [];

        actions.push({
          label: "ເບິ່ງລາຍລະອຽດ",
          icon: <EyeIcon className="h-4 w-4" />,
          onClick: () => onView(pkg),
        });

        if (canCancel) {
          actions.push({
            label: "ຍົກເລີກ",
            variant: "destructive" as const,
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ຍົກເລີກແພັກເກດ",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະຍົກເລີກແພັກເກດນີ້?",
                actionText: "ຍົກເລີກ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok) {
                onCancel(pkg.id);
              }
            },
          });
        }

        return <RowActions actions={actions} maxInline={2} />;
      },
    },
  ];

  return (
    <DataTable<PackageRow, unknown>
      noDataMessage="ບໍ່ພົບແພັກເກດ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
